export const COMPLAINT_FOLLOW_UP_CATEGORIES = [
  "body",
  "engine",
  "interior",
  "underbody",
  "tires",
  "brakes",
] as const;

export type ComplaintFollowUpCategory =
  (typeof COMPLAINT_FOLLOW_UP_CATEGORIES)[number];

export type ComplaintFollowUpValues = {
  damageLocation?: string;
  damageType?: string;
  engineComponent?: string;
  interiorSection?: string;
  wheelType?: string;
};

export function categoryHasFollowUps(
  id: string | undefined,
): id is ComplaintFollowUpCategory {
  return (
    !!id &&
    (COMPLAINT_FOLLOW_UP_CATEGORIES as readonly string[]).includes(id)
  );
}

export function isComplaintFollowUpComplete(
  category: string | undefined,
  values: ComplaintFollowUpValues,
): boolean {
  switch (category) {
    case "body":
    case "underbody":
    case "brakes":
      return Boolean(values.damageLocation && values.damageType);
    case "engine":
      return Boolean(values.engineComponent && values.damageType);
    case "interior":
      return Boolean(
        values.interiorSection && values.damageLocation && values.damageType,
      );
    case "tires":
      return Boolean(
        values.wheelType && values.damageLocation && values.damageType,
      );
    default:
      return true;
  }
}

export function emptyFollowUpValues(): ComplaintFollowUpValues {
  return {
    damageLocation: undefined,
    damageType: undefined,
    engineComponent: undefined,
    interiorSection: undefined,
    wheelType: undefined,
  };
}

const BODY_LIGHT = [
  "missing_part",
  "crack",
  "scratches",
  "non_original",
  "loose_fit",
] as const;
const BODY_BUMPER = [
  "missing_part",
  "crack",
  "dents",
  "scratches",
  "paint_damage",
  "respray",
  "non_original",
  "loose_fit",
] as const;
const BODY_PANEL = [
  "missing_part",
  "stone_chip",
  "crack",
  "dents",
  "scratches",
  "rust",
  "paint_damage",
  "respray",
  "non_original",
  "hail_under_10",
  "hail_over_10",
  "loose_fit",
  "wrapping",
  "faded_paint",
] as const;

export const BODY_PARTS = [
  "front_bumper",
  "front_spoiler",
  "radiator",
  "headlight_left",
  "headlight_right",
  "indicator_left",
  "indicator_right",
  "fog_light_left",
  "fog_light_right",
  "hood",
  "wiper_left",
  "wiper_right",
  "windshield",
  "windshield_frame",
  "side_skirts",
  "front_fender",
  "side_indicator",
  "mirror_housing",
  "mirror_glass",
  "front_door",
  "front_door_window",
  "front_door_handle",
  "a_pillar",
  "b_pillar",
  "c_pillar",
  "d_pillar",
  "rear_door",
  "rear_door_window",
  "rear_door_handle",
  "rear_side_window",
  "rear_fender",
  "fuel_cap",
  "rear_spoiler",
  "rear_bumper",
  "rear_panel",
  "trunk_lid",
  "rear_window",
  "rear_window_mount",
  "diffuser",
  "tail_light_left",
  "tail_light_right",
  "rear_fog_left",
  "rear_fog_right",
  "tow_hitch",
  "exhaust_pipe",
  "roof",
  "convertible_top",
  "sunroof",
  "roof_rails",
  "panoramic_roof",
  "antenna",
  "hardtop",
  "convertible_window",
  "roof_crossbars",
] as const;

export const BODY_PART_DAMAGE_TYPES: Record<string, readonly string[]> = {
  front_bumper: BODY_BUMPER,
  front_spoiler: BODY_BUMPER,
  radiator: ["missing_part", "leak", "non_original"],
  headlight_left: BODY_LIGHT,
  headlight_right: BODY_LIGHT,
  indicator_left: BODY_LIGHT,
  indicator_right: BODY_LIGHT,
  fog_light_left: BODY_LIGHT,
  fog_light_right: BODY_LIGHT,
  hood: BODY_PANEL,
  wiper_left: ["missing_part", "non_original", "loose_fit"],
  wiper_right: ["missing_part", "non_original", "loose_fit"],
  windshield: [
    "missing_part",
    "stone_chip",
    "crack",
    "non_original",
    "loose_fit",
  ],
  windshield_frame: [
    "rust",
    "paint_damage",
    "respray",
    "non_original",
    "loose_fit",
  ],
  side_skirts: [
    "missing_part",
    "crack",
    "dents",
    "scratches",
    "rust",
    "paint_damage",
    "respray",
    "non_original",
    "loose_fit",
    "wrapping",
  ],
  front_fender: BODY_PANEL,
  side_indicator: BODY_LIGHT,
  mirror_housing: [...BODY_BUMPER, "wrapping"],
  mirror_glass: BODY_LIGHT,
  front_door: BODY_PANEL,
  front_door_window: BODY_LIGHT,
  front_door_handle: [
    "missing_part",
    "scratches",
    "non_original",
    "loose_fit",
  ],
  a_pillar: ["rust", "paint_damage", "respray", "non_original", "loose_fit"],
  b_pillar: ["rust", "paint_damage", "respray", "non_original", "loose_fit"],
  c_pillar: ["rust", "paint_damage", "respray", "non_original", "loose_fit"],
  d_pillar: ["rust", "paint_damage", "respray", "non_original", "loose_fit"],
  rear_door: BODY_PANEL,
  rear_door_window: BODY_LIGHT,
  rear_door_handle: [
    "missing_part",
    "scratches",
    "non_original",
    "loose_fit",
  ],
  rear_side_window: BODY_LIGHT,
  rear_fender: BODY_PANEL,
  fuel_cap: [
    "missing_part",
    "scratches",
    "rust",
    "paint_damage",
    "respray",
    "non_original",
    "loose_fit",
  ],
  rear_spoiler: [...BODY_BUMPER, "wrapping"],
  rear_bumper: [...BODY_BUMPER, "wrapping"],
  rear_panel: [
    "missing_part",
    "crack",
    "dents",
    "scratches",
    "rust",
    "paint_damage",
    "respray",
    "non_original",
    "loose_fit",
    "wrapping",
  ],
  trunk_lid: BODY_PANEL,
  rear_window: BODY_LIGHT,
  rear_window_mount: [
    "missing_part",
    "rust",
    "paint_damage",
    "respray",
    "non_original",
    "loose_fit",
  ],
  diffuser: BODY_BUMPER,
  tail_light_left: BODY_LIGHT,
  tail_light_right: BODY_LIGHT,
  rear_fog_left: BODY_LIGHT,
  rear_fog_right: BODY_LIGHT,
  tow_hitch: ["missing_part", "rust", "non_original", "loose_fit"],
  exhaust_pipe: ["missing_part", "rust", "non_original", "loose_fit"],
  roof: BODY_PANEL,
  convertible_top: [
    "missing_part",
    "crack",
    "scratches",
    "leak",
    "non_original",
    "loose_fit",
  ],
  sunroof: [
    "missing_part",
    "crack",
    "scratches",
    "leak",
    "non_original",
    "loose_fit",
  ],
  roof_rails: ["missing_part", "rust", "non_original", "loose_fit"],
  panoramic_roof: [
    "missing_part",
    "crack",
    "scratches",
    "leak",
    "non_original",
    "loose_fit",
  ],
  antenna: ["missing_part", "non_original", "loose_fit"],
  hardtop: [
    "missing_part",
    "crack",
    "dents",
    "scratches",
    "rust",
    "paint_damage",
    "respray",
    "non_original",
    "loose_fit",
    "wrapping",
  ],
  convertible_window: BODY_LIGHT,
  roof_crossbars: ["missing_part", "rust", "non_original", "loose_fit"],
};

export const ENGINE_COMPONENTS = [
  "engine",
  "turbo",
  "transmission",
  "clutch",
  "cooling_system",
  "brake_fluid_level",
  "water_pump",
  "oil_pump",
  "fuel_system",
  "battery",
  "starter",
  "alternator",
  "air_conditioning",
  "power_steering",
  "exhaust_system",
  "spark_plugs",
  "timing_belt",
  "drive_belt",
] as const;

export const ENGINE_PROBLEMS: Record<string, readonly string[]> = {
  engine: [
    "too_loud",
    "abnormal_engine_noise",
    "low_oil_level",
    "load_change_vibrations",
    "low_power",
    "unusual_engine_noise",
    "oil_leak",
    "does_not_rotate",
  ],
  turbo: ["defective", "no_power", "missing_parts", "leaking"],
  transmission: [
    "too_loud",
    "abnormal_engine_noise",
    "low_oil_level",
    "load_change_vibrations",
    "low_power",
    "unusual_engine_noise",
    "oil_leak",
    "does_not_rotate",
  ],
  clutch: ["clutch_slipping", "defective", "heavy_pedal"],
  cooling_system: ["defective", "no_power", "missing_parts", "leaking"],
  brake_fluid_level: [
    "replacement_needed",
    "fluid_too_high",
    "fluid_too_low",
  ],
  water_pump: ["defective", "no_power", "missing_parts", "leaking"],
  oil_pump: [
    "low_oil_pressure",
    "abnormal_noise",
    "oil_leak",
    "total_failure",
  ],
  fuel_system: [
    "fuel_leak",
    "poor_performance",
    "starting_problems",
    "rough_idle",
  ],
  battery: [
    "not_charging",
    "dead_battery",
    "low_voltage",
    "corroded_terminals",
  ],
  starter: [
    "no_response",
    "grinding_noise",
    "slow_start",
    "irregular_function",
  ],
  alternator: [
    "not_charging",
    "warning_light",
    "irregular_voltage",
    "abnormal_noise",
  ],
  air_conditioning: [
    "no_cooling",
    "weak_airflow",
    "strange_smells",
    "unusual_noises",
  ],
  power_steering: [
    "heavy_steering",
    "abnormal_noise",
    "fluid_leak",
    "power_loss",
  ],
  exhaust_system: [
    "loud_noise",
    "exhaust_leak",
    "reduced_performance",
    "vibrations",
  ],
  spark_plugs: [
    "misfire",
    "poor_performance",
    "hard_to_start",
    "high_fuel_consumption",
  ],
  timing_belt: [
    "abnormal_noise",
    "belt_damage",
    "missing_teeth",
    "belt_too_loose",
  ],
  drive_belt: [
    "squealing_noise",
    "belt_damage",
    "belt_too_loose",
    "belt_missing",
  ],
};

export const INTERIOR_SECTIONS = [
  "interior",
  "control_elements",
  "trunk",
] as const;

export const INTERIOR_LOCATIONS: Record<string, readonly string[]> = {
  interior: [
    "front_door_interior_left",
    "front_door_interior_right",
    "door_interior_rear_left",
    "door_interior_rear_right",
    "driver_seat",
    "passenger_seat",
    "seat_back_left",
    "seat_back_right",
    "other_seats",
    "dashboard",
    "center_console",
    "headliner",
    "hat_rack",
    "floor_covering",
  ],
  control_elements: [
    "window_lifter",
    "key",
    "spare_key",
    "navigation_system",
    "rear_window_defroster",
    "front_windshield_defroster",
    "radio",
    "fan",
    "instrument_cluster",
    "horn",
    "window_wiper_washer",
    "warning_lights",
    "air_conditioning",
    "interior_lighting",
    "steering_wheel_lock",
    "hand_brake",
    "clutch_pedal_play",
    "abs_control_light",
    "esp_control_light",
    "airbag_control_light",
    "alarm_system",
    "seat_heating",
    "block_heater",
    "pdc",
    "central_lock_system",
    "electric_wing_mirrors",
    "cruise_control",
  ],
  trunk: [
    "spare_wheel",
    "trunk_floor_covering",
    "trunk_inside_cover_left",
    "trunk_inside_cover_right",
    "load_compartment_cover",
    "tire_emergency_kit",
  ],
};

const INTERIOR_SOFT = [
  "hole",
  "reconditioning_needed",
  "worn",
  "missing_part",
  "scratch",
  "burn",
  "dirty",
  "crack",
  "rupture",
] as const;
const INTERIOR_ELECTRICAL = [
  "not_working",
  "loose",
  "broken",
  "intermittent",
] as const;
const INTERIOR_DISPLAY = [
  "not_working",
  "display_fault",
  "loose",
  "broken",
] as const;

export const INTERIOR_DAMAGE_TYPES: Record<string, readonly string[]> = {
  front_door_interior_left: INTERIOR_SOFT,
  front_door_interior_right: INTERIOR_SOFT,
  door_interior_rear_left: INTERIOR_SOFT,
  door_interior_rear_right: INTERIOR_SOFT,
  driver_seat: [...INTERIOR_SOFT, "seat_heating_broken"],
  passenger_seat: [...INTERIOR_SOFT, "seat_heating_broken"],
  seat_back_left: INTERIOR_SOFT,
  seat_back_right: INTERIOR_SOFT,
  other_seats: INTERIOR_SOFT,
  dashboard: INTERIOR_SOFT,
  center_console: INTERIOR_SOFT,
  headliner: INTERIOR_SOFT,
  hat_rack: INTERIOR_SOFT,
  floor_covering: INTERIOR_SOFT,
  window_lifter: INTERIOR_ELECTRICAL,
  key: ["missing", "defective", "broken", "battery_fault"],
  spare_key: ["missing", "defective", "broken"],
  navigation_system: INTERIOR_DISPLAY,
  radio: [
    "not_working",
    "display_fault",
    "loose",
    "broken_button",
    "poor_sound",
  ],
  air_conditioning: ["not_working", "weak_effect", "noise", "leak"],
  central_lock_system: INTERIOR_ELECTRICAL,
  cruise_control: ["not_working", "loose", "broken_button", "intermittent"],
  rear_window_defroster: INTERIOR_ELECTRICAL,
  front_windshield_defroster: INTERIOR_ELECTRICAL,
  fan: ["not_working", "noise", "weak_effect"],
  instrument_cluster: INTERIOR_DISPLAY,
  horn: INTERIOR_ELECTRICAL,
  window_wiper_washer: INTERIOR_ELECTRICAL,
  warning_lights: INTERIOR_ELECTRICAL,
  interior_lighting: INTERIOR_ELECTRICAL,
  steering_wheel_lock: INTERIOR_ELECTRICAL,
  hand_brake: INTERIOR_ELECTRICAL,
  clutch_pedal_play: ["excessive_play", "loose", "not_working"],
  abs_control_light: INTERIOR_ELECTRICAL,
  esp_control_light: INTERIOR_ELECTRICAL,
  airbag_control_light: INTERIOR_ELECTRICAL,
  alarm_system: INTERIOR_ELECTRICAL,
  seat_heating: INTERIOR_ELECTRICAL,
  block_heater: INTERIOR_ELECTRICAL,
  pdc: INTERIOR_ELECTRICAL,
  electric_wing_mirrors: INTERIOR_ELECTRICAL,
  spare_wheel: ["missing_part", "damaged", "defective"],
  trunk_floor_covering: INTERIOR_SOFT,
  trunk_inside_cover_left: INTERIOR_SOFT,
  trunk_inside_cover_right: INTERIOR_SOFT,
  load_compartment_cover: INTERIOR_SOFT,
  tire_emergency_kit: ["missing_part", "replacement_needed"],
};

export const UNDERBODY_PARTS = [
  "underbody_left",
  "underbody_right",
  "wheel_bearing_front_left",
  "wheel_bearing_rear_left",
  "wheel_bearing_front_right",
  "wheel_bearing_rear_right",
  "driveshaft_front_left",
  "driveshaft_front_right",
  "driveshaft_rear_left",
  "driveshaft_rear_right",
  "brake_lines_front",
  "brake_lines_rear",
  "shock_absorber_front",
  "shock_absorber_rear",
  "exhaust_pipe",
  "catalytic_converter",
  "lambda_sensor",
  "muffler_front",
  "muffler_rear",
  "steering_and_ball_joints",
  "starter",
  "propeller_shaft",
  "fuel_tank",
] as const;

const UNDERBODY_RUST = ["rust", "damage"] as const;
const UNDERBODY_WEAR = ["noise", "wear", "defective"] as const;
const UNDERBODY_LEAK = ["rust", "leak", "damage"] as const;

export const UNDERBODY_DAMAGE_TYPES: Record<string, readonly string[]> = {
  underbody_left: UNDERBODY_RUST,
  underbody_right: UNDERBODY_RUST,
  wheel_bearing_front_left: UNDERBODY_WEAR,
  wheel_bearing_rear_left: UNDERBODY_WEAR,
  wheel_bearing_front_right: UNDERBODY_WEAR,
  wheel_bearing_rear_right: UNDERBODY_WEAR,
  driveshaft_front_left: UNDERBODY_WEAR,
  driveshaft_front_right: UNDERBODY_WEAR,
  driveshaft_rear_left: UNDERBODY_WEAR,
  driveshaft_rear_right: UNDERBODY_WEAR,
  brake_lines_front: UNDERBODY_LEAK,
  brake_lines_rear: UNDERBODY_LEAK,
  shock_absorber_front: ["leak", "wear", "defective"],
  shock_absorber_rear: ["leak", "wear", "defective"],
  exhaust_pipe: UNDERBODY_LEAK,
  catalytic_converter: ["defective", "damage"],
  lambda_sensor: ["defective"],
  muffler_front: ["rust", "leak", "noise"],
  muffler_rear: ["rust", "leak", "noise"],
  steering_and_ball_joints: ["wear", "noise", "defective"],
  starter: ["defective"],
  propeller_shaft: UNDERBODY_WEAR,
  fuel_tank: UNDERBODY_LEAK,
};

export const WHEEL_TYPES = ["car", "spare"] as const;

export const TIRE_POSITIONS = [
  "front_left",
  "front_right",
  "rear_left",
  "rear_right",
  "all_wheels",
] as const;

export const TIRE_AND_RIM_DAMAGE_TYPES = [
  "uneven_wear",
  "curb_damage",
  "illegal_dimension",
  "missing_parts",
  "age_cracks",
  "missing_part",
  "stone_chip",
  "crack",
  "dent",
  "non_original_equipment",
  "rust",
  "scratch",
  "respray",
] as const;

export const BRAKE_POSITIONS = [
  "all",
  "front_left",
  "front_right",
  "rear_left",
  "rear_right",
] as const;

export const BRAKE_DAMAGE_TYPES = [
  "worn_pads",
  "warped_discs",
  "rusty_discs",
  "leaking_brake_line",
  "broken_brake_hose",
  "poor_braking_power",
  "noise_when_braking",
] as const;
